import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Link, useSearchParams } from 'react-router-dom';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import api from '../api';
import './PowerBIDashboard.css';

const PowerBIDashboard = () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [embedConfig, setEmbedConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tokenExpiration, setTokenExpiration] = useState(null);
    const [dashboardName, setDashboardName] = useState('Dashboard');
    // Determine back navigation target: /home for single-dashboard users, /insights for multi
    const [backPath, setBackPath] = useState('/insights');
    // Dashboard id: from URL (when opened from Insights tile) or from state
    const dashboardId = searchParams.get('id') || location.state?.dashboardId;
    // Title: URL param is set when clicking Insights tile, so it's reliable
    const nameFromUrl = searchParams.get('name');
    const nameFromState = location.state?.dashboardName;
    const nameFromStorage = dashboardId ? sessionStorage.getItem(`powerbi-name-${dashboardId}`) : null;
    if (dashboardId && (nameFromUrl || nameFromState)) {
        const nameToStore = nameFromUrl || nameFromState;
        try {
            sessionStorage.setItem(`powerbi-name-${dashboardId}`, String(nameToStore));
        } catch (_) {}
    }
    const displayTitle = nameFromUrl || nameFromState || nameFromStorage || dashboardName;

    // Fetch user's assigned dashboard count to decide where back button goes
    useEffect(() => {
        const resolveBackPath = async () => {
            const selectedClient = localStorage.getItem('selectedClient');
            if (!selectedClient) return; // default stays '/insights'
            try {
                const accessRes = await api.get(`/api/admin/clients/${encodeURIComponent(selectedClient)}/powerbi/access`);
                if (!accessRes.data.hasPowerBIAccess) return;
                const dashRes = await api.get(`/api/admin/clients/${encodeURIComponent(selectedClient)}/powerbi/dashboards`);
                const count = (dashRes.data.dashboardIds || []).length;
                setBackPath(count <= 1 ? '/home' : '/insights');
            } catch (err) {
                console.error('[POWER BI] Error resolving back path:', err);
            }
        };
        resolveBackPath();
    }, []);

    const fetchEmbedToken = useCallback(async () => {
        try {
            const id = searchParams.get('id') || location.state?.dashboardId || 'dashboard_1';

            // Prefer clientName from URL search param (set when admin previews a specific client's
            // dashboard from AdminDashboard → passes clientName as URL param for correct RLS scoping).
            // Fall back to localStorage selectedClient for regular end-user embed flow.
            const clientNameFromUrl = searchParams.get('clientName') || '';
            const clientNameFromStorage = localStorage.getItem('selectedClient') || '';
            const selectedClient = clientNameFromUrl || clientNameFromStorage;
            const clientParam = selectedClient ? `&clientName=${encodeURIComponent(selectedClient)}` : '';

            const response = await api.get(`/api/powerbi/embed-token?dashboardId=${id}${clientParam}`);

            if (response.data.success) {
                
                const config = {
                    type: 'report',
                    id: response.data.reportId,
                    embedUrl: response.data.embedUrl,
                    accessToken: response.data.embedToken,
                    tokenType: models.TokenType.Embed,
                    settings: {
                        panes: {
                            filters: {
                                expanded: true,
                                visible: true
                            },
                            pageNavigation: {
                                visible: true
                            }
                        },
                        background: models.BackgroundType.Transparent,
                        layoutType: models.LayoutType.Custom
                    }
                };

                setEmbedConfig(config);
                setTokenExpiration(response.data.expiration);
                setLoading(false);
                setError(null);
            } else {
                throw new Error('Failed to get embed token');
            }
        } catch (err) {
            console.error('[POWER BI] ❌ Error fetching embed token:', err);
            console.error('[POWER BI] Error response:', err.response);
            
            let errorMessage = 'Failed to load Power BI dashboard';
            
            if (err.response?.status === 401) {
                errorMessage = 'Authentication failed. Please check Power BI Service Principal credentials in backend configuration.';
            } else if (err.response?.data?.details) {
                errorMessage = JSON.stringify(err.response.data.details, null, 2);
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
            setLoading(false);
        }
    }, [searchParams, location.state?.dashboardId]);

    const fetchDashboardName = useCallback(async () => {
        const id = searchParams.get('id') || location.state?.dashboardId;
        if (nameFromUrl || location.state?.dashboardName) return; // already have name from URL or state
        if (!id) return;
        try {
            // User-level API: returns { success, dashboards: [{ id, name, description }] }
            const response = await api.get('/api/powerbi/dashboards');
            const list = response.data?.dashboards || (Array.isArray(response.data) ? response.data : []) || [];
            const dashboard = list.find(d => d.id === id);
            const name = dashboard?.name || dashboard?.displayName || dashboard?.title;
            if (name) {
                const s = String(name);
                setDashboardName(s);
                try { sessionStorage.setItem(`powerbi-name-${id}`, s); } catch (_) {}
            }
        } catch (err) {
            console.error('[POWER BI] Error fetching dashboard name:', err);
        }
    }, [searchParams, location.state?.dashboardId, nameFromUrl, location.state?.dashboardName]);

    useEffect(() => {
        if (location.state?.dashboardName) {
            setDashboardName(location.state.dashboardName);
        }
    }, [location.state?.dashboardName]);

    useEffect(() => {
        fetchEmbedToken();
        fetchDashboardName();
    }, [fetchEmbedToken, fetchDashboardName]);

    useEffect(() => {
        // Set up token refresh interval (refresh 5 minutes before expiration)
        if (!tokenExpiration) return;

        const refreshInterval = setInterval(() => {
            const now = new Date();
            const expirationTime = new Date(tokenExpiration);
            const timeUntilExpiration = expirationTime - now;
            
            // Refresh if less than 5 minutes until expiration
            if (timeUntilExpiration < 5 * 60 * 1000 && timeUntilExpiration > 0) {
                fetchEmbedToken();
            }
        }, 60000); // Check every minute

        return () => clearInterval(refreshInterval);
    }, [tokenExpiration, fetchEmbedToken]);

    const handleError = (error) => {
        console.error('[POWER BI] Embed error:', error);
        console.error('[POWER BI] Error details:', JSON.stringify(error, null, 2));
        
        let errorMessage = 'Error loading Power BI report.';
        
        if (error.message) {
            errorMessage += ` ${error.message}`;
        }
        
        if (error.detailedMessage) {
            errorMessage += ` Details: ${error.detailedMessage}`;
        }
        
        console.error('[POWER BI] Setting error message:', errorMessage);
        setError(errorMessage);
    };

    if (loading) {
        return (
            <div className="powerbi-container">
                <div className="powerbi-back-bar">
                    <Link to={backPath} className="powerbi-back-button" aria-label="Back">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M5 12l7 7M5 12l7-7" />
                        </svg>
                    </Link>
                    <h1 className="powerbi-dashboard-title">{displayTitle}</h1>
                </div>
                <div className="powerbi-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading Power BI Dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="powerbi-container">
                <div className="powerbi-back-bar">
                    <Link to={backPath} className="powerbi-back-button" aria-label="Back">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M5 12l7 7M5 12l7-7" />
                        </svg>
                    </Link>
                    <h1 className="powerbi-dashboard-title">{displayTitle}</h1>
                </div>
                <div className="powerbi-error">
                    <div className="error-icon">⚠️</div>
                    <h2>Unable to Load Dashboard</h2>
                    <p>{error}</p>
                    <button onClick={fetchEmbedToken} className="retry-button">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="powerbi-container">
            <div className="powerbi-back-bar">
                <Link to={backPath} className="powerbi-back-button" aria-label="Back">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5M5 12l7 7M5 12l7-7" />
                    </svg>
                </Link>
                <h1 className="powerbi-dashboard-title">{displayTitle}</h1>
            </div>
            <div className="powerbi-embed-wrapper">
                {embedConfig && (
                    <PowerBIEmbed
                        embedConfig={embedConfig}
                        cssClassName="powerbi-report-container"
                        getEmbeddedComponent={(embeddedReport) => {
                            console.log('[POWER BI] ✅ Report embedded successfully');
                        }}
                        eventHandlers={
                            new Map([
                                ['loaded', () => console.log('[POWER BI] Report loaded')],
                                ['rendered', () => console.log('[POWER BI] Report rendered')],
                                ['error', handleError]
                            ])
                        }
                    />
                )}
            </div>
        </div>
    );
};

export default PowerBIDashboard;